import React from "react";
import { motion } from "framer-motion";

import {
  ShieldCheck,
  Zap,
  Code2,
  Layers3,
  Activity,
  LockKeyhole,
  ArrowUpRight,
} from "lucide-react";

const features = [
  {
    number: "01",
    icon: Code2,
    title: "Developer Friendly",
    description:
      "Integrate communication capabilities into your applications through clear API structures, predictable responses, and straightforward integration workflows.",
  },
  {
    number: "02",
    icon: ShieldCheck,
    title: "Secure Communication",
    description:
      "Build authentication and messaging workflows with controlled API access and security-focused communication infrastructure.",
  },
  {
    number: "03",
    icon: Zap,
    title: "Fast Response",
    description:
      "Keep critical customer workflows responsive with API infrastructure designed to process requests efficiently and return timely responses.",
  },
  {
    number: "04",
    icon: Layers3,
    title: "Scalable Infrastructure",
    description:
      "Support growing communication requirements with an API architecture designed to scale alongside your applications and business.",
  },
  {
    number: "05",
    icon: Activity,
    title: "Delivery Visibility",
    description:
      "Understand the status of your communication requests and use delivery information to build better customer and application workflows.",
  },
  {
    number: "06",
    icon: LockKeyhole,
    title: "Reliable Integration",
    description:
      "Connect your existing websites, applications, and backend systems with dependable APIs built for real-world business communication.",
  },
];

const WhyChooseUs = () => {
  return (
    <section
      className="
        relative
        overflow-hidden
        theme-bg-primary
        py-6
        sm:py-8
        md:py-10
        lg:py-14
      "
    >
      {/* =====================================================
          BACKGROUND GLOW
      ====================================================== */}

      <div
        className="
          pointer-events-none
          absolute
          -left-32
          top-24
          h-56
          w-56
          rounded-full
          bg-orange-500/[0.05]
          blur-[100px]
          sm:h-64
          sm:w-64
        "
      />

      <div
        className="
          pointer-events-none
          absolute
          -right-32
          bottom-20
          h-56
          w-56
          rounded-full
          bg-orange-500/[0.04]
          blur-[100px]
          sm:h-64
          sm:w-64
        "
      />

      {/* =====================================================
          MAIN CONTAINER
      ====================================================== */}

      <div
        className="
          relative
          mx-auto
          w-full
          max-w-7xl
          px-4
          sm:px-6
          lg:px-8
        "
      >
        {/* =================================================
            SECTION HEADER
        ================================================== */}

        <motion.div
          initial={{
            opacity: 0,
            y: 20,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
            amount: 0.2,
          }}
          transition={{
            duration: 0.6,
            ease: "easeOut",
          }}
          className="
            mx-auto
            mb-9
            max-w-2xl
            text-center
            sm:mb-10
            lg:mb-12
          "
        >
          {/* Label */}

          <motion.span
            initial={{
              opacity: 0,
              scale: 0.95,
            }}
            whileInView={{
              opacity: 1,
              scale: 1,
            }}
            viewport={{
              once: true,
            }}
            transition={{
              duration: 0.4,
            }}
            className="
              inline-flex
              rounded-full
              border
              border-orange-500/20
              bg-orange-500/[0.08]
              px-3
              py-1.5
              text-[10px]
              font-semibold
              uppercase
              tracking-[2px]
              sm:px-4
              sm:py-2
              sm:text-xs
              sm:tracking-[2.5px]
            "
            style={{
              color: "var(--brand-light)",
            }}
          >
            Why Choose Us
          </motion.span>

          {/* Heading */}

          <h2
            className="
              mt-4
              font-saira
              text-2xl
              font-bold
              leading-tight
              tracking-tight
              sm:mt-5
              sm:text-3xl
              md:text-4xl
              lg:text-5xl
            "
            style={{
              color: "var(--text-primary)",
            }}
          >
            Built For Reliable{" "}
            <span
              style={{
                color: "var(--brand-primary)",
              }}
            >
              Secure API Integration
            </span>
          </h2>

          {/* Description */}

          <p
            className="
              mx-auto
              mt-3
              max-w-xl
              text-xs
              leading-5
              sm:mt-4
              sm:text-sm
              sm:leading-6
              md:text-base
              md:leading-7
            "
            style={{
              color: "var(--text-secondary)",
            }}
          >
            Your communication infrastructure should work reliably in the
            background while your business focuses on its customers. Our API
            services are designed to simplify integration without compromising
            security, performance, or scalability.
          </p>
        </motion.div>

        {/* =================================================
            FEATURES
        ================================================== */}

        <div
          className="
            grid
            grid-cols-1
            md:grid-cols-2
            md:gap-x-8
          "
        >
          {features.map((item, index) => {
            const Icon = item.icon;

            return (
              <motion.div
                key={item.title}
                initial={{
                  opacity: 0,
                  y: 20,
                }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                }}
                viewport={{
                  once: true,
                  amount: 0.1,
                }}
                transition={{
                  duration: 0.5,
                  delay: index * 0.06,
                  ease: "easeOut",
                }}
                className="
                  group
                  relative
                  border-b
                  py-5
                  sm:py-6
                  lg:py-7
                  md:[&:nth-child(-n+2)]:border-t
                "
                style={{
                  borderColor: "var(--border-primary)",
                }}
              >
                {/* Hover Background */}

                <div
                  className="
                    pointer-events-none
                    absolute
                    -inset-x-3
                    inset-y-1
                    rounded-xl
                    bg-orange-500/[0.025]
                    opacity-0
                    blur-lg
                    transition-opacity
                    duration-300
                    group-hover:opacity-100
                  "
                />

                {/* =================================================
                    CONTENT ROW
                ================================================== */}

                <div
                  className="
                    relative
                    flex
                    items-start
                    gap-3
                    sm:gap-4
                  "
                >
                  {/* Number */}

                  <span
                    className="
                      w-7
                      shrink-0
                      pt-1
                      text-[10px]
                      font-semibold
                      tracking-[1.5px]
                      sm:w-9
                      sm:text-xs
                    "
                    style={{
                      color: "var(--brand-primary)",
                      opacity: 0.7,
                    }}
                  >
                    {item.number}
                  </span>

                  {/* Icon */}

                  <div
                    className="
                      flex
                      h-10
                      w-10
                      shrink-0
                      items-center
                      justify-center
                      rounded-lg
                      border
                      transition-all
                      duration-300
                      group-hover:bg-orange-500
                      group-hover:text-white
                      sm:h-11
                      sm:w-11
                      sm:rounded-xl
                    "
                    style={{
                      borderColor: "var(--border-primary)",
                      backgroundColor: "var(--card-bg-secondary)",
                      color: "var(--brand-primary)",
                    }}
                  >
                    <Icon
                      size={19}
                      strokeWidth={1.8}
                      className="
                        transition-transform
                        duration-300
                        group-hover:scale-110
                      "
                    />
                  </div>

                  {/* Text Content */}

                  <div className="min-w-0 flex-1">
                    {/* Title + Arrow */}

                    <div
                      className="
                        flex
                        items-start
                        justify-between
                        gap-2
                      "
                    >
                      <h3
                        className="
                          font-saira
                          text-base
                          font-semibold
                          leading-snug
                          transition-colors
                          duration-300
                          group-hover:text-orange-400
                          sm:text-lg
                        "
                        style={{
                          color: "var(--text-primary)",
                        }}
                      >
                        {item.title}
                      </h3>

                      <ArrowUpRight
                        size={16}
                        className="
                          mt-0.5
                          shrink-0
                          transition-all
                          duration-300
                          group-hover:-translate-y-0.5
                          group-hover:translate-x-0.5
                          group-hover:text-orange-500
                          sm:h-[18px]
                          sm:w-[18px]
                        "
                        style={{
                          color: "var(--text-muted)",
                        }}
                      />
                    </div>

                    {/* Description */}

                    <p
                      className="
                        mt-2
                        max-w-xl
                        text-xs
                        leading-5
                        transition-colors
                        duration-300
                        group-hover:text-zinc-400
                        sm:mt-2.5
                        sm:text-sm
                        sm:leading-6
                      "
                      style={{
                        color: "var(--text-muted)",
                      }}
                    >
                      {item.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* =================================================
            BOTTOM TRUST LINE
        ================================================== */}

        <motion.div
          initial={{
            opacity: 0,
          }}
          whileInView={{
            opacity: 1,
          }}
          viewport={{
            once: true,
          }}
          transition={{
            duration: 0.5,
            delay: 0.2,
          }}
          className="
            mt-7
            flex
            flex-wrap
            items-center
            justify-center
            gap-2
            text-center
            sm:mt-9
            sm:gap-3
          "
        >
          <div className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map((star) => (
              <span
                key={star}
                className="
                  text-xs
                  sm:text-sm
                "
                style={{
                  color: "var(--brand-primary)",
                }}
              >
                ★
              </span>
            ))}
          </div>

          <span
            className="
              text-xs
              sm:text-sm
            "
            style={{
              color: "var(--text-muted)",
            }}
          >
            Reliable API infrastructure for modern businesses
          </span>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyChooseUs;